'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import {
	PollingDriftClientAccountSubscriber,
	CustomizedCadenceBulkAccountLoader,
} from '@drift-labs/sdk';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import Env from '../environmentVariables/EnvironmentVariables';
import { needsDataRequirement } from '../utils/routeDataRequirements';

/**
 * Optimizes bulk account loader polling frequency based on current route.
 * Reduces polling frequency on read-only pages to minimize network requests.
 * Only applies to polling mode - websocket mode is unaffected.
 */
const useOptimizedAccountLoader = () => {
	const pathname = usePathname();
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const walletPublicKey = useDriftStore(
		(s) => s.wallet.current.adapter?.publicKey
	);

	useEffect(() => {
		if (!pathname || !walletPublicKey || !driftClient) return;

		const accountSubscriber = driftClient.accountSubscriber;
		if (!accountSubscriber) return;

		// Only optimize polling mode - websocket mode doesn't use account loader cadence
		if (accountSubscriber instanceof PollingDriftClientAccountSubscriber) {
			// Check if it's using the customizable bulk account loader
			if (
				accountSubscriber.accountLoader instanceof
				CustomizedCadenceBulkAccountLoader
			) {
				// Check if current route needs frequent account updates
				const needsFrequentUpdates = needsDataRequirement(
					pathname,
					'transacting'
				);

				// Base polling frequency
				const baseFrequency = Env.pollingFrequencyMs;

				// Use different multipliers based on page type
				const frequency = needsFrequentUpdates
					? baseFrequency * 1 // Full speed for transacting pages
					: baseFrequency * 3; // 3x slower for read-only pages

				// Update the account loader cadence
				accountSubscriber.accountLoader.setCustomPollingFrequency(
					walletPublicKey,
					frequency
				);
			}
		}
		// If using websocket mode, no optimization needed - websockets are already efficient
	}, [pathname, walletPublicKey, driftClient]);
};

export default useOptimizedAccountLoader;
